import { ECompassDirection, EGameOrientation, EPresetKeyboardAction, type IPresetKeyboardMap, type TCompassFivePointsData } from '../types/control';
import { generateRandomId } from '../utils';


export function ratioXY (x: number, y: number, width: number, height: number) {
  return {
    x: Math.round((x / width) * 1000),
    y: Math.round((y / height) * 1000)
  }
}

export function calcXAndY (x: number, y: number, width: number, height: number, isHorizontal: boolean) {
  const { x: deviceX, y: deviceY } = ratioXY(x, y, width, height);
  if (isHorizontal) {
    return {
      x: Math.abs(deviceY - 1000),
      y: deviceX
    }
  }

  return { x: deviceX, y: deviceY };
}

function calcMouseXAndY (pageX: number, pageY: number, rect: { left: number, top: number, width: number, height: number }, isHorizontal: boolean) {
  const { left, top, width, height } = rect;
  const x = Math.round(pageX - left);
  const y = Math.round(pageY -  top);
  return calcXAndY(x, y, width, height, isHorizontal);
}


/*
https://developer.mozilla.org/en-US/docs/Web/API/Element/mousemove_event
Q: why do not use MouseEvent.layerX or MouseEvent.layerY?
A: Maybe some browser cannot support.
*/
export function convertElementCoord2DeviceToCoord (e: MouseEvent | TouchEvent, rect: { left: number, top: number, width: number, height: number }, isHorizontal: boolean) {
  let pageX = 0;
  let pageY = 0;
  let xyArr = [];

  if (!e.type.includes('touch')) {
    // pc
    pageX = (e as MouseEvent).pageX;
    pageY = (e as MouseEvent).pageY;
    xyArr.push({ id: 0, ...calcMouseXAndY(pageX, pageY, rect, isHorizontal) });
  } else {
    // mobile / pad
    (e.type === 'touchstart') && e.preventDefault();
    const isTouchEnd = e.type === 'touchend';
    const touches = isTouchEnd ? (e as TouchEvent).changedTouches : (e as TouchEvent).touches;

    if (touches.length) {
      for (let i = 0; i < touches.length; i++) {
        xyArr.push({ id: touches[i].identifier, ...calcMouseXAndY(touches[i].pageX, touches[i].pageY, rect, isHorizontal) });
      }
    }
  }

  return xyArr;
}

export function convertCompassCoord2DeviceToCoord(compassInfo: {
  fivePoints: TCompassFivePointsData,
  hotPress: Set<ECompassDirection>,
  prevHotPressSize: number,
  deviceCoord?: { x: number, y: number }
}, isHorizontal: boolean) {
  const { hotPress, deviceCoord, fivePoints, prevHotPressSize } = compassInfo;
  let _deviceCoord = deviceCoord || { x: fivePoints.centerX, y: fivePoints.centerY };

  // pressup reset center
  if (prevHotPressSize !== hotPress.size) {
    const { centerX: x, centerY: y } = fivePoints;
    _deviceCoord = { x, y }
  }

  // if (prevHotPressSize !== hotPress.size) {
  //   if (!hotPress.has(ECompassDirection.LEFT) || !hotPress.has(ECompassDirection.RIGHT)) {
  //     _deviceCoord.y = fivePoints.centerY;
  //   } else if (!hotPress.has(ECompassDirection.UP) || !hotPress.has(ECompassDirection.DOWN)) {
  //     _deviceCoord.x = fivePoints.centerX;
  //   }
  // }

  // arrow four direction
  const KEY_UP = isHorizontal ? ECompassDirection.UP : ECompassDirection.RIGHT;
  const KEY_DOWN = isHorizontal ? ECompassDirection.DOWN : ECompassDirection.LEFT;
  const KEY_LEFT = isHorizontal ? ECompassDirection.LEFT : ECompassDirection.UP;
  const KEY_RIGHT = isHorizontal ? ECompassDirection.RIGHT : ECompassDirection.DOWN;

  if (hotPress.has(KEY_UP)) {
    // _deviceCoord = { x: fivePoints.topX, y: fivePoints.topY }
    _deviceCoord = _deviceCoord.x < fivePoints.topX ? { x: _deviceCoord.x += 10, y: _deviceCoord.y } : _deviceCoord;
  }

  if (hotPress.has(KEY_DOWN)) {
    // _deviceCoord = { x: fivePoints.bottomX, y: fivePoints.bottomY }
    _deviceCoord = _deviceCoord.x > fivePoints.bottomX ? { x: _deviceCoord.x -= 10, y: _deviceCoord.y } : _deviceCoord;
  }

  if (hotPress.has(KEY_LEFT)) {
    // _deviceCoord = { x: fivePoints.leftX, y: fivePoints.leftY }
    _deviceCoord = _deviceCoord.y > fivePoints.leftY ? { x: _deviceCoord.x, y: _deviceCoord.y -= 10 } : _deviceCoord;
  }

  if (hotPress.has(KEY_RIGHT)) {
    // _deviceCoord = { x: fivePoints.rightX, y: fivePoints.rightY }
    _deviceCoord = _deviceCoord.y < fivePoints.rightY ? { x: _deviceCoord.x, y: _deviceCoord.y += 10 } : _deviceCoord;
  }

  return _deviceCoord;
}

export function calcAreaCenterXY (size: number, x: number, y: number, videoWidth: number, videoHeight: number, isHorizontal: boolean) {
  const ratio = isHorizontal ? videoHeight / videoWidth : videoWidth / videoHeight;
  const deviceX = isHorizontal ? Math.abs(y - 1000) : x;
  const deviceY = isHorizontal ? x : y;

  // radius
  const deviceYRadius = Math.round(size / 2);
  const deviceXRadius = Math.round((size / ratio) / 2);
  return {
    deviceYRadius,
    deviceXRadius,
    centerX: deviceX + (isHorizontal ? -deviceXRadius : deviceYRadius),
    centerY: deviceY + (isHorizontal ? size : deviceYRadius) / 2
  }
}

export function calcCompassFivePoint (size: number, presetX: number, presetY: number, videoWidth: number, videoHeight: number, isHorizontal: boolean) {
  // radius & center
  const { deviceYRadius, deviceXRadius, centerX, centerY } = calcAreaCenterXY(size, presetX, presetY, videoWidth, videoHeight, isHorizontal);

  // left -y
  let leftX = centerX;
  let leftY = centerY - deviceYRadius;

  // right +y
  let rightX = centerX;
  let rightY = centerY + deviceYRadius;

  // top +x
  let topX = centerX + deviceXRadius;
  let topY = centerY;

  // bottom -x
  let bottomX = centerX - deviceXRadius;
  let bottomY = centerY;

  return {
    centerX,
    centerY,
    leftX,
    leftY,
    rightX,
    rightY,
    topX,
    topY,
    bottomX,
    bottomY
  }
}

const spacialKey = ['escape', 'space', 'enter', 'shift', 'alt', 'tab', 'control', '`'];
const spacialKeyMap = {
  'escape': ['Escape'],
  'space': ['Space'],
  'enter': ['Enter', 'NumpadEnter'],
  'shift': ['ShiftLeft', 'ShiftRight'],
  'alt': ['AltLeft', 'AltRight'],
  'tab': ['Tab'],
  'control': ['ControlLeft', 'ControlRight'],
  '`': ['Backquote']
}

export function oldKeyConfig2NewKeyConfig(input: string | Record<string, never>, orientation: EGameOrientation) {
  let presetKeyMap: IPresetKeyboardMap = {
    interactive: {},
    logic: {}
  };

  if (typeof input === 'string') {
    input = JSON.parse(input);
  }

  if (input?.logic) return input as unknown as IPresetKeyboardMap;

  const oldRatio = (orientation === EGameOrientation.PORTRAIT ? 272 : 856) / 1000;

  Object.keys(input).map(key => {
    const item = input[key];
    let keyName = key.split('___')[1];
    let keyCode = `Key${keyName.toUpperCase()}`;

    if (keyName === 'wasd') {
      const w = presetKeyMap.logic['KeyW'] || [];
      const a = presetKeyMap.logic['KeyA'] || [];
      const s = presetKeyMap.logic['KeyS'] || [];
      const d = presetKeyMap.logic['KeyD'] || [];

      const interactiveId = generateRandomId(presetKeyMap.interactive);
      presetKeyMap.interactive[interactiveId] = {
        code: ['KeyW', 'KeyA', 'KeyS', 'KeyD'],
        width: item.size / oldRatio,
        height: item.size / oldRatio,
        x: item.left,
        y: item.top,
        name: {
          [ECompassDirection.UP]: 'W',
          [ECompassDirection.LEFT]: 'A',
          [ECompassDirection.DOWN]: 'S',
          [ECompassDirection.RIGHT]: 'D'
        },
        zIndex: 1,
        type: EPresetKeyboardAction.COMPASS
      }

      presetKeyMap.logic['KeyW'] = [...w, {
        patch: EPresetKeyboardAction.COMPASS,
        direction: ECompassDirection.UP,
        interactive: interactiveId,
      }];

      presetKeyMap.logic['KeyA'] = [...a, {
        patch: EPresetKeyboardAction.COMPASS,
        direction: ECompassDirection.LEFT,
        interactive: interactiveId,
      }];

      presetKeyMap.logic['KeyS'] = [...s, {
        patch: EPresetKeyboardAction.COMPASS,
        direction: ECompassDirection.DOWN,
        interactive: interactiveId,
      }];

      presetKeyMap.logic['KeyD'] = [...d, {
        patch: EPresetKeyboardAction.COMPASS,
        direction: ECompassDirection.RIGHT,
        interactive: interactiveId,
      }];

    } else if (!isNaN(Number(keyName))) {
      const digitKey = `Digit${keyName}`;
      const numpadKey = `Numpad${keyName}`;
      const digitKeyData = presetKeyMap.logic[digitKey] || [];
      const numpadKeyData = presetKeyMap.logic[numpadKey] || [];

      const digitInteractiveId = generateRandomId(presetKeyMap.interactive);
      presetKeyMap.interactive[digitInteractiveId] = {
        code: [digitKey],
        width: 40 / oldRatio,
        height: 40 / oldRatio,
        x: item.left,
        y: item.top,
        name: digitKey,
        zIndex: 1,
        type: EPresetKeyboardAction.TOUCH
      };

      const numpadInteractiveId = generateRandomId(presetKeyMap.interactive);
      presetKeyMap.interactive[numpadInteractiveId] = {
        code: [numpadKey],
        width: 40 / oldRatio,
        height: 40 / oldRatio,
        x: item.left,
        y: item.top,
        name: numpadKey,
        zIndex: 1,
        type: EPresetKeyboardAction.TOUCH
      };

      presetKeyMap.logic[digitKey] = [...digitKeyData, {
        patch: EPresetKeyboardAction.TOUCH,
        interactive: digitInteractiveId,
      }];

      presetKeyMap.logic[numpadKey] = [...numpadKeyData, {
        patch: EPresetKeyboardAction.TOUCH,
        interactive: numpadInteractiveId,
      }];

    } else if (spacialKey.includes(keyName.toLocaleLowerCase())) {
      const newKeyCodeList = spacialKeyMap[keyName.toLocaleLowerCase() as keyof typeof spacialKeyMap];
      newKeyCodeList.map(v => {
        const keyData = presetKeyMap.logic[v] || [];
        const interactiveId = generateRandomId(presetKeyMap.interactive);
        presetKeyMap.interactive[interactiveId] = {
          code: [v],
          width: 40 / oldRatio,
          height: 40 / oldRatio,
          x: item.left,
          y: item.top,
          name: v,
          zIndex: 1,
          type: EPresetKeyboardAction.TOUCH
        }
        presetKeyMap.logic[v] = [...keyData, {
          patch: EPresetKeyboardAction.TOUCH,
          interactive: interactiveId
        }]
      });
    } else {
      const keyData = presetKeyMap.logic[keyCode] || [];
      const interactiveId = generateRandomId(presetKeyMap.interactive);
      presetKeyMap.interactive[interactiveId] = {
        code: [keyCode],
        width: 40 / oldRatio,
        height: 40 / oldRatio,
        x: item.left,
        y: item.top,
        name: keyName,
        zIndex: 1,
        type: EPresetKeyboardAction.TOUCH
      }
      presetKeyMap.logic[keyCode] = [...keyData, {
        patch: EPresetKeyboardAction.TOUCH,
        interactive: interactiveId
      }]
    }
  });
  return presetKeyMap
}
